<div class="solution-card"
     [class.previewed]="this.solution.state.previewed"
     [class.applied]="this.solution.state.applied"
     [class.blocked]="this.solution.state.blocked"
     [class.modified]="this.isModified"
     [class.target-gone]="this.isTargetGone"
     [class.aggregate-locked]="this.aggregateLocked"
     [class.expanded]="this.expanded">

  <!-- Package title bar: name (left) + net price (right). Clicking it collapses or expands the
       PACKAGE. Both survive the collapse — only the description, the package total and the action
       row are stripped (owner, 2026-07-27). -->
  <div class="title-bar" (click)="this.toggleExpanded()">
    <!-- Name and LEG COUNT travel together in their own group, and the group is what stretches.
         Sibling spans of the title bar cannot do this: the name span itself stretches, so a plain
         sibling lands at the far edge next to the price. The group ellipsises the NAME only, so
         "(9 legs)" survives however long the title gets (owner, 2026-08-19). -->
    <span class="title-group">
      <span class="name" [title]="this.solution.content.displayName">{{ this.solution.content.displayName }}</span>
      <!-- The TRADED leg count — see packageLegsLabel for why it is not the sum of the cards. -->
      <span class="legs-total">{{ this.packageLegsLabel }}</span>
      <!-- Owner 2026-09-03: a package holding a NAKED LEG (a one-leg order) is flagged in the title
           bar; the open package's description says why (see .naked-warning below). -->
      <i class="fas fa-exclamation-circle naked-icon" *ngIf="this.hasNakedLeg"
         title="Contains a Naked Leg. Execute That Order Before the Others."></i>
    </span>
    <span class="price"
          [class.credit]="this.netComboPrice > 0"
          [class.debit]="this.netComboPrice < 0">{{ this.formatMoney(this.netComboPrice) }}</span>
  </div>

  <!-- COLLAPSED, with cards: the cards as CHIPS, in a row. Name and price only, plus the leg count
       and expiration — the codified breakdown belongs to the open package. The whole row expands
       the package rather than opening one chip: a chip is not individually collapsible until the
       package around it is open. -->
  <div class="chip-row" *ngIf="!this.expanded && this.hasPackageCards" (click)="this.toggleExpanded()">
    <div class="chip" *ngFor="let card of this.packageCards">
      <div class="chip-head">
        <span class="chip-name" [title]="card.title">{{ card.title }}</span>
        <i class="fas fa-exclamation-circle naked-icon" *ngIf="this.isNakedCard(card)"
           title="Naked Leg - Execute This Order First."></i>
        <span class="price"
              [class.credit]="this.cardOrderPrice(card) > 0"
              [class.debit]="this.cardOrderPrice(card) < 0">{{ this.formatMoney(this.cardOrderPrice(card)) }}</span>
      </div>
      <div class="chip-sub">{{ this.cardLegsLabel(card) }} · {{ this.cardExpiration(card) }}</div>
    </div>
  </div>

  <!-- COLLAPSED, no cards: the pre-package summary row (Close tab, LLM path). -->
  <div class="summary-row" *ngIf="!this.expanded && !this.hasPackageCards" (click)="this.toggleExpanded()">
    <span class="short-desc">{{ this.solution.content.shortDescription }}</span>
    <span class="codified-preview">{{ this.codifiedPreview }}</span>
  </div>

  <div class="detail" *ngIf="this.expanded">

    <div class="badges">
      <span class="badge previewed" *ngIf="this.solution.state.previewed">Previewed</span>
      <span class="badge applied" *ngIf="this.solution.state.applied">Applied</span>
      <span class="badge blocked" *ngIf="this.solution.state.blocked">Blocked</span>
      <span class="badge modified" *ngIf="this.isModified">Modified</span>
      <span class="badge gone" *ngIf="this.isTargetGone">Hedge Removed</span>
    </div>

    <!-- The reason behind the title bar's exclamation mark: one of the package's orders is a
         single naked leg and must be executed before the other orders. -->
    <div class="naked-warning" *ngIf="this.hasNakedLeg">
      <i class="fas fa-exclamation-circle"></i>
      This Package Contains a Naked Leg ({{ this.nakedCardTitles }}). If You're Going to Execute This
      Package, Make Sure You Execute This Order First, or Try an Optimal Order Arrangement to Get
      Rid of This Naked Leg.
    </div>

    <!-- Always shown: the package cannot collapse, so its description is never hidden. -->
    <div class="full-description">{{ this.fullDescription }}</div>

    <!-- THE CARDS. Each is a card in its own right — its own name, its own price, its own legs —
         and each collapses and expands on its own. They carry no actions: the cards of a package
         are necessary together and are never traded separately. -->
    <div class="inner-card" *ngFor="let card of this.packageCards" [class.expanded]="card.expanded">

      <div class="inner-card-head" (click)="this.toggleCard(card)">
        <span class="inner-card-name">{{ card.title }}</span>
        <!-- The naked-leg card itself is marked, not only named in the package warning (owner). -->
        <i class="fas fa-exclamation-circle naked-icon" *ngIf="this.isNakedCard(card)"
           title="Naked Leg - Execute This Order First."></i>
        <span class="price"
              [class.credit]="this.cardOrderPrice(card) > 0"
              [class.debit]="this.cardOrderPrice(card) < 0">{{ this.formatMoney(this.cardOrderPrice(card)) }}</span>
      </div>

      <div class="inner-card-summary" *ngIf="!card.expanded" (click)="this.toggleCard(card)">
        <span class="legs-count">{{ this.cardLegsLabel(card) }} · {{ this.cardExpiration(card) }}</span>
        <span class="codified-preview">{{ this.cardCodifiedPreview(card) }}</span>
      </div>

      <table class="legs" *ngIf="card.expanded">
        <thead>
          <tr><th>Action</th><th>Instrument</th><th>Qty</th><th>Price</th></tr>
        </thead>
        <tbody>
          <tr *ngFor="let leg of card.legs">
            <td>{{ leg.action }}</td>
            <td>{{ this.instrumentName(leg) }}</td>
            <td class="qty">{{ this.signedQuantity(leg) }}</td>
            <td class="price"
                [class.credit]="this.legSignedPrice(leg) > 0"
                [class.debit]="this.legSignedPrice(leg) < 0">{{ this.formatMoney(this.legSignedPrice(leg)) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="3">Total (Order Price)</td>
            <td class="price"
                [class.credit]="this.cardOrderPrice(card) > 0"
                [class.debit]="this.cardOrderPrice(card) < 0">{{ this.formatMoney(this.cardOrderPrice(card)) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="package-total" *ngIf="this.hasPackageCards">
      <span class="label">Package Total (Order Price)</span>
      <span class="price"
            [class.credit]="this.orderComboPrice > 0"
            [class.debit]="this.orderComboPrice < 0">{{ this.formatMoney(this.orderComboPrice) }}</span>
    </div>

    <!-- No cards: the pre-package rendering, unchanged (LLM path, locally-generated Closes). -->
    <table class="legs" *ngIf="!this.hasPackageCards">
      <thead>
        <tr><th>Action</th><th>Instrument</th><th>Qty</th><th>Price</th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let leg of this.sortedLegs">
          <td>{{ leg.action }}</td>
          <td>{{ this.instrumentName(leg) }}</td>
          <td class="qty">{{ this.signedQuantity(leg) }}</td>
          <td class="price"
              [class.credit]="this.legSignedPrice(leg) > 0"
              [class.debit]="this.legSignedPrice(leg) < 0">{{ this.formatMoney(this.legSignedPrice(leg)) }}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="3">Total (Order Price)</td>
          <td class="price"
              [class.credit]="this.orderComboPrice > 0"
              [class.debit]="this.orderComboPrice < 0">{{ this.formatMoney(this.orderComboPrice) }}</td>
        </tr>
      </tfoot>
    </table>

    <div class="actions">
      <dx-button [text]="this.previewLabel" [disabled]="this.previewDisabled" (onClick)="this.onPreview()" width="100%"></dx-button>
      <dx-button [text]="this.applyLabel" [disabled]="this.applyDisabled" (onClick)="this.onApply()" width="100%"></dx-button>
      <dx-button text="Transactions" [disabled]="this.transactionsDisabled" (onClick)="this.onTransactions()" width="100%"></dx-button>
    </div>

    <!-- M8 (CR-20260720-001): compliance disclaimer, under the action row so it is on screen before
         the hedge is previewed or applied. Text is the SHARED constant
         (copilot-shared/copilot-disclaimer.ts) — one source of truth for all five render sites. -->
    <div class="card-disclaimer" data-element="card-disclaimer" *ngIf="this.disclaimerEnabled"><span class="disclaimer-label">{{ this.disclaimer.label }}</span>: {{ this.disclaimer.text }}</div>

  </div>

</div>

<!-- Transactions popup: kept OUTSIDE the collapse/expand gating so the @ViewChild always resolves (the
     Transactions button lives in the expanded detail; the popup must exist when it is clicked).
     Read-only before -> transaction -> after view; copy is intentionally absent (FR-92/93/95/96). -->
<ets-hg-copilot-transactions-popup></ets-hg-copilot-transactions-popup>
